import { useLayoutEffect, useRef } from 'react'
import { getAppScrollElement } from '../utils/appScroll'
import { readStopScroll, writeStopScroll, type StopKey } from '../stores/stopMemory'

/**
 * ADR-106 P4 — a swipe-chain stop's own scroll position on the app's single
 * scrolling surface (`<main id="app-scroll">`, ADR-067 Decision 3).
 *
 * On entering the stop the surface is put at the top straight away — which
 * ends the old accident of one screen's offset carrying into the next. A stop
 * visited before this session then gets its remembered offset back, but only
 * once `ready` says its content has rendered (never against a skeleton), and
 * clamped to what exists if the list got shorter meanwhile.
 *
 * `restore: false` means one of the values that defines the stop's list had
 * to fall back on the way back in (a deleted folder, a page past the end, a
 * level or language no longer offered — owner decision for P4): the old
 * offset belonged to a different list, so the stop stays at the top.
 *
 * While the stop is showing, every scroll is recorded, so leaving by any route
 * — tap, back, a link into a deck — needs no "save on the way out", which
 * could only read an offset the next screen's content has already clamped.
 * Nothing is recorded until the stop's own position has been settled, so a
 * scroll the browser makes while the old content is swapped out is never
 * mistaken for the reader's.
 */
export function useStopScroll(
  /** `null` when the component is not showing a stop of its own right now. */
  stop: StopKey | null,
  { ready, restore = true }: { ready: boolean; restore?: boolean },
): void {
  /** The offset still to be applied once `ready`; null when nothing is pending. */
  const pending = useRef<number | null>(null)
  const settled = useRef(false)

  useLayoutEffect(() => {
    const el = getAppScrollElement()
    if (!el || stop === null) return

    const saved = readStopScroll(stop)
    el.scrollTop = 0
    pending.current = saved !== undefined && saved > 0 ? saved : null
    settled.current = pending.current === null

    const onScroll = () => {
      if (settled.current) writeStopScroll(stop, el.scrollTop)
    }
    el.addEventListener('scroll', onScroll)
    return () => {
      el.removeEventListener('scroll', onScroll)
      pending.current = null
      settled.current = false
    }
  }, [stop])

  useLayoutEffect(() => {
    if (stop === null || pending.current === null || !ready) return
    const el = getAppScrollElement()
    if (!el) return
    const target = pending.current
    pending.current = null
    if (restore) {
      // Clamp to what exists: the browser would too, but saying it keeps the
      // recorded value honest when the list got shorter meanwhile.
      el.scrollTop = Math.max(0, Math.min(target, el.scrollHeight - el.clientHeight))
    } else {
      el.scrollTop = 0
    }
    settled.current = true
    writeStopScroll(stop, el.scrollTop)
  }, [stop, ready, restore])
}
